import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import moment from "moment-timezone";

import { useListCustomers } from "@/lib/api/customer";
import { CommonDestinationTimezone } from "@/lib/utils";

import { WizardShell } from "./components/WizardShell";
import { runMatcher } from "./matching";
import { PhaseFinalReview } from "./phases/PhaseFinalReview";
import { PhaseManualGrouping } from "./phases/PhaseManualGrouping";
import { PhaseReviewSuggested } from "./phases/PhaseReviewSuggested";
import { PhaseUploadMatch } from "./phases/PhaseUploadMatch";
import { PhaseUploading } from "./phases/PhaseUploading";
import {
  getBlockingErrorsForPhase,
  getGroupsReadyForUpload,
} from "./state/selectors";
import {
  makeInitialWizardState,
  wizardReducer,
} from "./state/wizardReducer";
import type { CustomerMinimal, FileLocationRef } from "./state/types";
import { useWizardUploader } from "./upload/useWizardUploader";

type CreateBatchWizardProps = {
  /** Called when the operator chooses to leave the wizard entirely. */
  onExit: () => void;
};

type CustomerRow = {
  id: string;
  long_name: string | null;
};

/**
 * Full-page customer-matching wizard.
 *
 * Hosts the local `useReducer` wizard state, the customer-loading
 * react-query call, and the upload-bridge hook. Renders the active
 * phase.
 *
 * Phase 0 (customer load) is component-level, not a reducer phase:
 *   - Customers are loaded on mount via `useListCustomers({ minimal: true })`.
 *   - File matching is blocked until the customer list is loaded.
 *
 * The HTML period input gives `YYYY-MM`; we convert to `YYYY-MM-01`
 * before dispatching `SET_PERIOD` so `WizardData.period_date` matches
 * the spec exactly.
 */
export function CreateBatchWizard({ onExit }: CreateBatchWizardProps) {
  const [state, dispatch] = useReducer(wizardReducer, makeInitialWizardState());

  // Upload-bridge hook owns the per-group status map and exposes the
  // `startAll` pool entry point. Status is rendered by `PhaseUploading`.
  const { statusByGroupId, startAll } = useWizardUploader();

  // Guard against starting two upload runs from a single phase-entry.
  // React 18 strict mode runs effects twice in dev; a re-render while
  // already in `uploading` would otherwise fire `startAll` again. The
  // ref is reset on RESET so a fresh wizard run can upload.
  const uploadStartedRef = useRef<boolean>(false);

  // Pre-match local state: the period month (YYYY-MM, HTML input format)
  // and the raw file list. Only persisted to wizard state once `MATCH_FILES`
  // fires, at which point matching has run and `FileEntry` records exist.
  const [periodMonth, setPeriodMonth] = useState<string>(() =>
    moment.tz(CommonDestinationTimezone).format("YYYY-MM"),
  );
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [matchingInProgress, setMatchingInProgress] = useState<boolean>(false);

  // Seed the reducer with the default current-month period so the
  // "Match & continue" button is not blocked on first paint.
  useEffect(() => {
    if (state.data.period_date !== "") return;
    if (state.phase !== "select") return;
    if (periodMonth === "") return;
    const parsed = moment.tz(periodMonth, "YYYY-MM", true, CommonDestinationTimezone);
    if (!parsed.isValid()) return;
    const periodDate = parsed.startOf("month").format("YYYY-MM-DD");
    dispatch({ type: "SET_PERIOD", period_date: periodDate });
    // We intentionally only run this when the relevant pieces change;
    // the reducer guard makes it a no-op if the phase has moved on.
  }, [state.data.period_date, state.phase, periodMonth]);

  const customersQuery = useListCustomers({ minimal: true });

  const customersLoading =
    customersQuery.isLoading || customersQuery.isFetching;
  const customersResult = customersQuery.data;
  const customersError = useMemo((): string | null => {
    if (customersQuery.isError) {
      return customersQuery.error instanceof Error
        ? customersQuery.error.message
        : "Unknown error";
    }
    if (customersResult && customersResult.success === false) {
      return customersResult.message || "Failed to load customers";
    }
    return null;
  }, [customersQuery.isError, customersQuery.error, customersResult]);

  // Spec invariant: matching inputs are only `id` and `long_name`.
  // Customers with no long_name are dropped from the matching pool to
  // avoid bogus suggestions against blank phrases.
  const customers: CustomerMinimal[] = useMemo(() => {
    if (!customersResult || customersResult.success === false) return [];
    const rows = (customersResult.data ?? []) as CustomerRow[];
    const out: CustomerMinimal[] = [];
    for (const row of rows) {
      if (row.long_name === null || row.long_name === "") continue;
      out.push({ id: row.id, long_name: row.long_name });
    }
    return out;
  }, [customersResult]);

  const handleAddFiles = useCallback((files: File[]) => {
    setSelectedFiles((prev: File[]) => [...prev, ...files]);
  }, []);

  const handleClearFiles = useCallback(() => {
    setSelectedFiles([]);
  }, []);

  const handlePeriodChange = useCallback((nextMonth: string) => {
    setPeriodMonth(nextMonth);
    if (nextMonth === "") {
      dispatch({ type: "SET_PERIOD", period_date: "" });
      return;
    }
    const parsed = moment.tz(nextMonth, "YYYY-MM", true, CommonDestinationTimezone);
    if (!parsed.isValid()) {
      // Reducer-level guard accepts the empty period as "not set".
      dispatch({ type: "SET_PERIOD", period_date: "" });
      return;
    }
    const periodDate = parsed.startOf("month").format("YYYY-MM-DD");
    dispatch({ type: "SET_PERIOD", period_date: periodDate });
  }, []);

  const handleRunMatch = useCallback(() => {
    if (state.phase !== "select") return;
    if (state.data.period_date === "") return;
    if (selectedFiles.length === 0) return;
    if (customers.length === 0) return;

    setMatchingInProgress(true);
    // Matching is synchronous and fast for typical inputs. We still
    // wrap the dispatch in a microtask so the "Matching…" label has a
    // chance to paint for large inputs.
    Promise.resolve().then(() => {
      const result = runMatcher({ files: selectedFiles, customers });
      dispatch({ type: "MATCH_FILES", result });
      setMatchingInProgress(false);
    });
  }, [state.phase, state.data.period_date, selectedFiles, customers]);

  const handleDiscard = useCallback(() => {
    dispatch({ type: "RESET" });
    setSelectedFiles([]);
    setPeriodMonth(moment.tz(CommonDestinationTimezone).format("YYYY-MM"));
    setMatchingInProgress(false);
    uploadStartedRef.current = false;
    onExit();
  }, [onExit]);

  // Phase 2 handlers — thin wrappers around dispatch so the phase
  // component stays decoupled from the reducer action shape.
  const handleSelectCustomer = useCallback(
    (groupId: string, customerId: string | null) => {
      dispatch({
        type: "SELECT_CUSTOMER",
        group_id: groupId,
        customer_id: customerId,
      });
    },
    [],
  );
  const handleApproveGroup = useCallback((groupId: string) => {
    dispatch({ type: "APPROVE_GROUP", group_id: groupId });
  }, []);
  const handleMarkNeedsEdit = useCallback((groupId: string) => {
    dispatch({ type: "MARK_NEEDS_EDIT", group_id: groupId });
  }, []);
  const handleContinueToManual = useCallback(() => {
    dispatch({ type: "CONTINUE_TO_MANUAL" });
  }, []);

  // Phase 3 handlers — SELECT_CUSTOMER is shared with Phase 2 above.
  const handleMoveFile = useCallback(
    (
      fileId: string,
      source: FileLocationRef,
      target: FileLocationRef,
    ) => {
      dispatch({ type: "MOVE_FILE", file_id: fileId, source, target });
    },
    [],
  );
  const handleCreateManualGroup = useCallback((fileIds: string[]) => {
    dispatch({ type: "CREATE_MANUAL_GROUP", file_ids: fileIds });
  }, []);
  const handleDissolveGroup = useCallback((groupId: string) => {
    dispatch({ type: "DISSOLVE_GROUP", group_id: groupId });
  }, []);
  const handleContinueToFinal = useCallback(() => {
    dispatch({ type: "CONTINUE_TO_FINAL" });
  }, []);

  // Phase 4 handlers.
  const handleBackToManual = useCallback(() => {
    dispatch({ type: "BACK_TO_MANUAL" });
  }, []);
  const handleStartUpload = useCallback(() => {
    // Defensive guard — the reducer also re-checks via
    // getBlockingErrorsForPhase(state.data, "final_review"), but we
    // early-return here so we don't even dispatch when something is
    // wrong (and so this stays explicit at the call site).
    if (state.phase !== "final_review") return;
    const errors = getBlockingErrorsForPhase(state.data, "final_review");
    if (errors.length > 0) return;
    dispatch({ type: "START_UPLOAD" });
  }, [state.phase, state.data]);

  // When entering the uploading phase, kick off the pool exactly once.
  // The ref guard prevents re-entry from React 18 strict-mode double
  // effects or other re-renders while still in `uploading`.
  useEffect(() => {
    if (state.phase !== "uploading") return;
    if (uploadStartedRef.current) return;
    const groupsReady = getGroupsReadyForUpload(state.data);
    if (groupsReady.length === 0) return;
    uploadStartedRef.current = true;
    void startAll({
      groups: groupsReady,
      files: state.data.files,
      period_date: state.data.period_date,
    });
  }, [state.phase, state.data, startAll]);

  return (
    <WizardShell
      phase={state.phase}
      subtitle="Match PDFs to customers, then upload one batch per customer."
      onDiscard={handleDiscard}
      discardDisabled={state.phase === "uploading"}
    >
      {state.phase === "select" ? (
        <PhaseUploadMatch
          period_month={periodMonth}
          selected_files={selectedFiles}
          customers_loading={customersLoading}
          customers_error={customersError}
          customers_count={customers.length}
          matching_in_progress={matchingInProgress}
          onPeriodChange={handlePeriodChange}
          onAddFiles={handleAddFiles}
          onClearFiles={handleClearFiles}
          onRunMatch={handleRunMatch}
        />
      ) : null}

      {state.phase === "review_suggested" ? (
        <PhaseReviewSuggested
          data={state.data}
          customers={customers}
          blocking_errors={getBlockingErrorsForPhase(
            state.data,
            "review_suggested",
          )}
          onSelectCustomer={handleSelectCustomer}
          onApproveGroup={handleApproveGroup}
          onMarkNeedsEdit={handleMarkNeedsEdit}
          onContinue={handleContinueToManual}
        />
      ) : null}

      {state.phase === "manual_grouping" ? (
        <PhaseManualGrouping
          data={state.data}
          customers={customers}
          blocking_errors={getBlockingErrorsForPhase(
            state.data,
            "manual_grouping",
          )}
          onSelectCustomer={handleSelectCustomer}
          onMoveFile={handleMoveFile}
          onCreateManualGroup={handleCreateManualGroup}
          onDissolveGroup={handleDissolveGroup}
          onContinue={handleContinueToFinal}
        />
      ) : null}

      {state.phase === "final_review" ? (
        <PhaseFinalReview
          data={state.data}
          customers={customers}
          blocking_errors={getBlockingErrorsForPhase(
            state.data,
            "final_review",
          )}
          onBack={handleBackToManual}
          onStartUpload={handleStartUpload}
        />
      ) : null}

      {state.phase === "uploading" ? (
        <PhaseUploading
          data={state.data}
          customers={customers}
          statusByGroupId={statusByGroupId}
          onDone={onExit}
        />
      ) : null}
    </WizardShell>
  );
}
